import { useEffect, useState } from 'react';
import { Plus, Trash2, Save, Power, EyeOff, DollarSign } from 'lucide-react';
import supabase from '../lib/supabase';
import {
  PageHeader, Section, Badge, Button, EmptyState, Modal, Field, Input, Select, StatusLine,
} from '../components/ui';

type Rate = {
  id: string;
  coach_id: string;
  lesson_type: 'private' | 'semi_private' | 'group';
  environment: 'indoor' | 'outdoor';
  session_minutes: number;
  per_person_price: number;
  currency: string;
  max_participants: number | null;
  is_active: boolean;
};

export default function CoachRates({ coachId }: { coachId: string }) {
  const [rows, setRows] = useState<Rate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('coach_lesson_types')
      .select('*')
      .eq('coach_id', coachId)
      .order('lesson_type')
      .order('environment');
    setRows((data as Rate[]) ?? []);
    setLoading(false);
  };

  useEffect(() => { load(); }, [coachId]);

  const toggleActive = async (r: Rate) => {
    await supabase.from('coach_lesson_types').update({ is_active: !r.is_active }).eq('id', r.id);
    load();
  };

  const del = async (r: Rate) => {
    if (!confirm(`Delete this ${r.lesson_type.replace('_', ' ')} / ${r.environment} rate?`)) return;
    await supabase.from('coach_lesson_types').delete().eq('id', r.id);
    load();
  };

  return (
    <div className="p-8 max-w-5xl">
      <PageHeader
        eyebrow="Coach"
        title="Rates & packages"
        subtitle="One row per lesson type x environment x duration. Customers see only active rates."
        actions={<Button icon={Plus} onClick={() => setShowAdd(true)}>Add rate</Button>}
      />

      <Section className="p-0 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-gray-500">Loading rates…</div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={DollarSign}
            title="No rates published yet"
            description="Add your first rate so customers can book a lesson at the right length and price."
            action={<Button icon={Plus} onClick={() => setShowAdd(true)}>Add your first rate</Button>}
          />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50/70 text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Lesson type</th>
                <th className="text-left px-5 py-3 font-semibold">Environment</th>
                <th className="text-left px-5 py-3 font-semibold">Duration</th>
                <th className="text-right px-5 py-3 font-semibold">Per person</th>
                <th className="text-left px-5 py-3 font-semibold">Max</th>
                <th className="text-left px-5 py-3 font-semibold">Status</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50/60">
                  <td className="px-5 py-3 capitalize">{r.lesson_type.replace('_', ' ')}</td>
                  <td className="px-5 py-3 capitalize">{r.environment}</td>
                  <td className="px-5 py-3">{r.session_minutes} min</td>
                  <td className="px-5 py-3 text-right font-medium">
                    {r.currency} {Number(r.per_person_price).toFixed(0)}
                  </td>
                  <td className="px-5 py-3 text-gray-700">{r.max_participants ?? '—'}</td>
                  <td className="px-5 py-3">
                    <button onClick={() => toggleActive(r)}>
                      {r.is_active
                        ? <Badge tone="green" icon={Power}>Active</Badge>
                        : <Badge tone="gray" icon={EyeOff}>Hidden</Badge>}
                    </button>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <Button size="sm" variant="danger" icon={Trash2} onClick={() => del(r)}>Delete</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      {showAdd && (
        <RateForm
          coachId={coachId}
          onClose={() => setShowAdd(false)}
          onSaved={async () => { setShowAdd(false); await load(); }}
        />
      )}
    </div>
  );
}

function RateForm({
  coachId, onClose, onSaved,
}: { coachId: string; onClose: () => void; onSaved: () => void }) {
  const [lessonType, setLessonType] = useState<Rate['lesson_type']>('private');
  const [environment, setEnvironment] = useState<Rate['environment']>('outdoor');
  const [duration, setDuration] = useState('60');
  const [price, setPrice] = useState('350');
  const [maxP, setMaxP] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    const payload = {
      coach_id: coachId,
      lesson_type: lessonType,
      environment,
      session_minutes: Number(duration),
      per_person_price: Number(price),
      max_participants: maxP ? Number(maxP) : null,
    };
    const { error } = await supabase.from('coach_lesson_types').insert([payload]);
    setBusy(false);
    if (error) setErr(error.message);
    else onSaved();
  };

  return (
    <Modal title="Add rate" subtitle="Price per person. For group lessons the total is price x players." onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Lesson type">
            <Select value={lessonType} onChange={(e) => setLessonType(e.target.value as Rate['lesson_type'])} className="capitalize">
              <option value="private">Private</option>
              <option value="semi_private">Semi-private</option>
              <option value="group">Group</option>
            </Select>
          </Field>
          <Field label="Environment">
            <Select value={environment} onChange={(e) => setEnvironment(e.target.value as Rate['environment'])} className="capitalize">
              <option value="outdoor">Outdoor</option>
              <option value="indoor">Indoor</option>
            </Select>
          </Field>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Duration">
            <Select value={duration} onChange={(e) => setDuration(e.target.value)}>
              <option value="30">30 min</option>
              <option value="60">60 min</option>
              <option value="90">90 min</option>
              <option value="120">120 min</option>
            </Select>
          </Field>
          <Field label="Per-person (AED)">
            <Input type="number" min="0" value={price} onChange={(e) => setPrice(e.target.value)} required />
          </Field>
          <Field label="Max players">
            <Input type="number" min="1" value={maxP} onChange={(e) => setMaxP(e.target.value)}
              placeholder={lessonType === 'private' ? '1' : lessonType === 'semi_private' ? '2' : '6'} />
          </Field>
        </div>

        {err && <StatusLine kind="error">{err}</StatusLine>}

        <div className="flex gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose} className="flex-1">Cancel</Button>
          <Button type="submit" loading={busy} icon={Save} className="flex-1">Add rate</Button>
        </div>
      </form>
    </Modal>
  );
}
